/* global fetch */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { createUseStyles } from 'react-jss'
import Flickity from 'react-flickity-component'
import gsap from 'gsap'
import { LinkWithCursorHoverEffect } from '../Link'
import isEmpty from 'lodash/isEmpty'
import theme from '../../style/theme'
import { span, sliceMarginStyles } from '../../style/span'
import { useDragCursor } from '../Cursor'
import Heading from '../Heading'
import cn from 'classnames'

const preventVerticalScrolling = (flickity) => {
  // This will prevent the vertical scrolling when scrolling in flickity
  flickity.on('dragStart', () => { document.ontouchmove = e => e.preventDefault() })
  flickity.on('dragEnd', () => { document.ontouchmove = () => true })
}
const FLICKITY_OPTIONS = {
  cellAlign: 'left',
  pageDots: false,
  contain: true,
  freeScroll: true,
  prevNextButtons: true,
  adaptiveHeight: false,
  groupCells: true,
  wrapAround: false,
  selectedAttraction: 0.015,
  friction: 0.25,
  percentPosition: true,
  autoPlay: 0
}
/**
 * InstagramFeedSlider component - displays Instagram posts in a slider
 */
const InstagramFeedSlider = ({ className, title = 'Track us like you’re stalking your next meal...', link, flickityOptions = FLICKITY_OPTIONS, onChange, ...rest }) => {
  const classes = useStyles()
  const containerRef = useRef()
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Define your parameters using the values you provided
  const apiParams = {
    limit: 12, // Example: fetch 12 posts (you can adjust this)
    account: 'discovered-wildfoods.myshopify.com', // Your specified Shopify domain
    fu: 0,
    hash: '01981c2a-537e-741a-b209-b934a08fbf6f' // Your specified hash
    // Optional parameters (uncomment and set if needed, otherwise they'll be omitted)
    // fid: 0, // Default feed ID
    // admin: 0 // Default for non-admin requests
  }

  // Construct the query string from the parameters
  const queryString = new URLSearchParams(apiParams).toString()
  const INSTAFEED_API_ENDPOINT = `https://instafeed.nfcube.com/feed/v6?${queryString}`

  const setFlickityRef = useCallback((flickity) => {
    if (flickity) {
      gsap.set(flickity.element, { display: 'block', overflow: 'visible' })
      // Flickity consumes the mouse move events so we need to create a customer event so the custom mouse
      // cursor continues to follow the mouse cursor
      flickity.on('dragMove', (event, pointer, moveVector) => {
        const dragMoveEvent = new window.CustomEvent('flickity-dragMove', { detail: event })
        window.dispatchEvent(dragMoveEvent)
      })
      preventVerticalScrolling(flickity)

      if (onChange) {
        flickity.on('change', onChange)
      }
    }
  }, [onChange])

  useDragCursor(containerRef)

  useEffect(() => {
    const fetchInstagramPosts = async () => {
      try {
        const response = await fetch(INSTAFEED_API_ENDPOINT)

        if (!response.ok) {
          // If the response is not OK (e.g., 404, 500), throw an error
          const errorText = await response.text() // Get response body for more details
          throw new Error(`HTTP error! Status: ${response.status}. Message: ${errorText}`)
        }

        const data = await response.json()
        // console.log('Instafeed API Response:', data) // Log the full response to understand its structure

        // Extract posts based on your actual API structure
        let postsData = []
        if (Array.isArray(data)) {
          postsData = data
        } else if (data && Array.isArray(data.data)) {
          postsData = data.data
        } else if (data && Array.isArray(data.posts)) {
          postsData = data.posts
        } else if (data && Array.isArray(data.items)) {
          postsData = data.items
        } else {
          console.warn('Instagram API response structure not recognized:', data)
          postsData = []
        }

        // console.log('Extracted posts data:', postsData)
        setPosts(postsData)
      } catch (err) {
        console.error('Failed to fetch Instagram posts:', err)
        setError(err)
        setPosts([]) // Ensure posts is always an array
      } finally {
        setLoading(false)
      }
    }

    fetchInstagramPosts()
  }, [INSTAFEED_API_ENDPOINT]) // Dependency array ensures useEffect re-runs if endpoint URL changes
  if (loading) {
    return (
      <section className={cn(className, classes.section)}>
        <div className={classes.loadingContainer}>
          <div className={classes.loader} />
          <p>Loading Instagram feed...</p>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className={cn(className, classes.section)}>
        <div className={classes.errorContainer}>
          <p>Error loading Instagram feed: {error.message}</p>
        </div>
      </section>
    )
  }

  if (posts.length === 0) {
    return (
      <section className={cn(className, classes.section)}>
        <div className={classes.emptyContainer}>
          <p>No Instagram posts found for this account.</p>
        </div>
      </section>
    )
  }

  return (
    <section className={cn(className, classes.section)}>
      {/* Mobile layout - stacked */}
      <div className={classes.mobileLayout}>
        {title && <Heading text={title} className={classes.mobileTitle} />}
        {!isEmpty(link) && (
          <LinkWithCursorHoverEffect link={link} className={classes.mobileLink}>
            View on Instagram
          </LinkWithCursorHoverEffect>
        )}

        <div ref={containerRef}>
          <Flickity
            options={flickityOptions}
            disableImagesLoaded
            reloadOnUpdate
            static
            className={classes.flickity}
            flickityRef={setFlickityRef}
            {...rest}
          >
            {posts.map((post) => (
              <InstagramPost
                key={post.id || post.media_id || post.uid}
                post={post}
                className={classes.postTile}
              />
            ))}
          </Flickity>
        </div>
      </div>

      {/* Desktop layout - Title full width, then content */}
      <div className={classes.desktopLayout}>
        {/* Full width title at top */}
        {title && (
          <div className={classes.fullWidthTitleContainer}>
            <Heading key={title} text={title} className={classes.desktopTitle} />
            <p className={classes.desktopTitle}>@discovered_foods</p>
          </div>
        )}

        {/* Two column layout below title */}
        <div className={classes.twoColumnContainer}>
          {/* Left column - Link (1/4 width) */}
          {/* <div className={classes.leftColumn}>
            {!isEmpty(link) && (
              <LinkWithCursorHoverEffect link={link} className={classes.desktopLink}>
                View on Instagram
              </LinkWithCursorHoverEffect>
            )}
          </div> */}

          {/* Right column - Slider (3/4 width) */}
          <div className={classes.sliderColumn}>
            <div ref={containerRef}>
              <Flickity
                options={flickityOptions}
                disableImagesLoaded
                reloadOnUpdate
                static
                className={classes.flickity}
                flickityRef={setFlickityRef}
                {...rest}
              >
                {posts.map((post) => (
                  <InstagramPost
                    key={post.id || post.media_id || post.uid}
                    post={post}
                    className={classes.postTile}
                  />
                ))}
              </Flickity>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * InstagramPost component - individual post tile
 */
const InstagramPost = ({ post, className }) => {
  const classes = useStyles()

  // Extract image URL based on your API structure
  const getImageUrl = (post) => {
    // For carousel posts, use the first image
    if (post.carousel && post.carousel.length > 0) {
      return post.carousel[0].media_url
    }
    // For single images, use the standard resolution
    if (post.images && post.images.standard_resolution) {
      return post.images.standard_resolution.url
    }
    // Fallback to other possible image fields
    return post.media_url || post.image || post.src
  }

  // Extract caption text
  const getCaptionText = (post) => {
    if (post.caption && post.caption.text) {
      return post.caption.text
    }
    return post.caption || ''
  }

  const imageUrl = getImageUrl(post)
  const captionText = getCaptionText(post)

  return (
    <div className={cn(className, classes.post)}>
      <a
        href={post.link}
        target='_blank'
        rel='noopener noreferrer'
        className={classes.postLink}
      >
        <div className={classes.imageContainer}>
          <img
            src={imageUrl}
            alt={captionText || 'Instagram Post'}
            className={classes.postImage}
            onError={(e) => {
              console.error('Instagram image failed to load:', imageUrl)
              e.target.src = '/placeholder-instagram.jpg' // Add a placeholder image
            }}
          />
        </div>
        {captionText && (
          <div className={classes.captionContainer}>
            <p className={classes.caption}>
              {captionText.length > 100 ? `${captionText.substring(0, 100)}...` : captionText}
            </p>
          </div>
        )}
      </a>
    </div>
  )
}

const useStyles = createUseStyles({
  section: {
    extend: [sliceMarginStyles],
    padding: [0, span(1)],
    overflowX: 'hidden',
    [theme.breakpoints.up('md')]: {
      padding: [0, span(1, 'md')]
    }
  },

  // Mobile layout - stacked vertically
  mobileLayout: {
    display: 'block',
    [theme.breakpoints.up('lg')]: {
      display: 'none'
    }
  },

  // Desktop layout - Title full width, then 2 columns
  desktopLayout: {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      display: 'block'
    }
  },

  // Full width title container
  fullWidthTitleContainer: {
    [theme.breakpoints.up('lg')]: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      marginBottom: '25px'
    }
  },

  // Two column container below title
  twoColumnContainer: {
    [theme.breakpoints.up('lg')]: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '40px',
      minHeight: '400px'
    }
  },

  // Left column - Link (1/4 width)
  leftColumn: {
    [theme.breakpoints.up('lg')]: {
      width: '20%',
      flexShrink: 0,
      paddingRight: '40px'
    }
  },

  // Right column - Slider (3/4 width)
  sliderColumn: {
    [theme.breakpoints.up('lg')]: {
      width: '100%',
      flexShrink: 0,
      overflow: 'visible'
    }
  },

  flickity: {
    outline: 'none',
    whiteSpace: 'nowrap',
    display: 'flex',
    padding: [0, 0],
    '& > *': {
      flexShrink: 0
    },
    // Improved navigation buttons for Instagram slider
    '& .flickity-prev-next-button': {
      top: '40%',
      transform: 'translateY(-50%)',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 1)'
      }
    }
  },

  // Mobile styles
  mobileTitle: {
    whiteSpace: 'normal',
    display: 'block',
    color: theme.colors.primary,
    fontSize: '30px',
    fontWeight: 'bold',
    fontFamily: theme.fonts.headings,
    marginBottom: '20px'
  },

  mobileLink: {
    display: 'inline-block',
    textTransform: 'uppercase',
    textDecoration: 'none',
    position: 'relative',
    margin: [0, 0, '20px', 0],
    fontSize: '16px',
    ...theme.link(),
    [theme.breakpoints.up('lg')]: {
      display: 'none'
    }
  },

  // Desktop styles
  desktopTitle: {
    fontSize: '16px',
    lineHeight: '1.3em',
    color: theme.colors.primary,
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    marginBottom: '4px !important'
  },

  desktopLink: {
    display: 'inline-block',
    textTransform: 'uppercase',
    textDecoration: 'none',
    position: 'relative',
    ...theme.link(),
    fontSize: '14px',
    marginTop: span(0.5, 'md')
  },

  // Post tile styles
  post: {
    display: 'block',
    whiteSpace: 'normal',
    color: theme.colors.textSecondary,
    width: span(9),
    position: 'relative',
    margin: '0px',
    transform: 'rotate(0deg)',
    overflow: 'hidden',
    background: '#fff',
    cursor: 'pointer',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    [theme.breakpoints.up('md')]: {
      width: span(7, 'md'),
      margin: '0px'
    },
    [theme.breakpoints.up('lg')]: {
      width: span(6, 'md')
    },
    '&:hover': {
      transform: 'translateY(-2px)'
    }
  },

  postLink: {
    display: 'block',
    textDecoration: 'none',
    color: 'inherit'
  },

  imageContainer: {
    overflow: 'hidden',
    marginBottom: '0px',
    position: 'relative',
    paddingBottom: '100%', // Square aspect ratio for Instagram posts
    width: '100%',
    background: '#f5f5f5',
    [theme.breakpoints.up('md')]: {
      marginBottom: '0px'
    },
    '& > img': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%'
    }
  },

  postImage: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    transition: 'transform 1.6s ease-in-out',
    '$post:hover &': {
      transform: 'scale(1.05)'
    }
  },

  captionContainer: {
    padding: [span(0.5), 0],
    minHeight: '60px',
    display: 'none'
  },

  caption: {
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    lineHeight: 1.4,
    color: theme.colors.primary,
    margin: 0,
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },

  // Loading and error states
  loadingContainer: {
    textAlign: 'center',
    padding: '40px 20px',
    '& p': {
      marginTop: '20px',
      color: theme.colors.grey
    }
  },

  loader: {
    border: '3px solid rgba(56, 82, 79, 0.1)',
    borderRadius: '50%',
    borderTop: '3px solid #38524F',
    width: '40px',
    height: '40px',
    animation: '$spin 1s linear infinite',
    margin: '0 auto'
  },

  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' }
  },

  errorContainer: {
    textAlign: 'center',
    padding: '40px 20px',
    color: theme.colors.error || '#e74c3c',
    '& p': {
      margin: 0
    }
  },

  emptyContainer: {
    textAlign: 'center',
    padding: '40px 20px',
    color: theme.colors.grey,
    '& p': {
      margin: 0
    }
  }
}, { name: 'InstagramFeedSlider' })

export default InstagramFeedSlider
